/**
 * OPVisual LIVE - what OPVisual itself REPORTS (DEC-272), as opposed to
 * `session.ts`, which holds what OPButtons SENT.
 *
 * OPVisual publishes `opvisual_status` frames to OPBridge (its CONTRACT.md
 * section 8): `state` on every change (per lane: on_air, armed, enabled,
 * opacity, blend, speed; plus blackout, master, look, cam_shake), `grid_fired`
 * / `grid_column` when a fire LANDS, and a 1 Hz `heartbeat` (fps, dropped
 * frames as a running total). OPBridge keeps the last `state` (served by
 * `GET /opvisual/state`) and relays every frame to the consumer sockets of the
 * bridge OPVisual is attached to.
 *
 * Two feeds, one store, keyed by bridge row id:
 * - the sockets (`consumerWs.ts` for the primary, `peerPwaSockets.ts` for every
 *   other paired bridge) deliver frames as they happen;
 * - the listing poll (`listing.ts`, every 5 s while a grid is on screen)
 *   delivers OPBridge's remembered `state`, which covers a socket that is not
 *   attached to OPVisual's bridge.
 *
 * Trust rule (`opvLiveUsable`): the reported state is used only while there is
 * reason to believe OPVisual is there NOW: a heartbeat within `ALIVE_MS`, or
 * the last poll saying OPVisual is connected. Otherwise the grid falls back to
 * its SENT marks, exactly as before. A light never claims "on air" from a
 * remembered frame of an OPVisual that has since gone.
 *
 * Plain signal over an immutable record (a Solid read of a Map tracks nothing).
 */

import { createSignal } from 'solid-js';
import { resolveBridge } from '../opbridge/store.ts';
import type { OpvLookParam } from './wire.ts';

export interface OpvLaneLive {
  /** 1-based, L1 = bottom. */
  lane: number;
  assetId: string | null;
  onAir: boolean;
  armed: boolean;
  enabled: boolean | null;
  opacity: number | null;
  blend: string | null;
  speed: number | null;
}

/** One of OPVisual's show palettes, as its `state.palettes` lists them (2026-10-28, OPVisual CONTRACT 4c):
 *  `swatch` is four `#rrggbb` colours, dark to bright. */
export interface OpvPalette { id: string; name: string; swatch: string[] }

export interface OpvLive {
  /** When the last `state` arrived (local ms), or 0 if none yet. */
  stateAt: number;
  /** Every palette a `palette` verb can name, in OPVisual's picker order (empty until an OPVisual that sends them
   *  reports). The list is constant, so a frame without it keeps the last one. */
  palettes: OpvPalette[];
  /** The show palette's id as OPVisual last reported it, or null. */
  palette: string | null;
  lanes: OpvLaneLive[];
  blackout: boolean | null;
  master: number | null;
  look: Partial<Record<OpvLookParam, number>>;
  camShake: { on: boolean; amount: number } | null;
  /** Last column a fire LANDED in, per layer (from `grid_fired` / `grid_column`). */
  landedCol: Record<number, number>;
  /** Heartbeat: last fps, when it came, and recent (time, running dropped total) pairs. */
  fps: number | null;
  heartbeatAt: number;
  dropHistory: Array<[number, number]>;
  /** What the last `GET /opvisual/state` said about OPVisual being attached. */
  polledConnected: boolean | null;
  polledAt: number;
}

/** A heartbeat younger than this means OPVisual is running now. */
export const ALIVE_MS = 4000;
/** A poll younger than this may vouch for OPVisual (the poll runs every 5 s). */
const POLL_TRUST_MS = 12_000;
/** Frames dropped within this window count as "dropping now". */
export const DROP_WINDOW_MS = 10_000;

function empty(): OpvLive {
  return {
    stateAt: 0, palettes: [], palette: null, lanes: [], blackout: null, master: null, look: {}, camShake: null,
    landedCol: {}, fps: null, heartbeatAt: 0, dropHistory: [], polledConnected: null, polledAt: 0,
  };
}

const [liveSig, setLiveSig] = createSignal<Record<string, OpvLive>>({});

/** Tests and the bot. */
export function resetOpvLive(): void { setLiveSig({}); }

function update(bridgeRowId: string, fn: (cur: OpvLive) => OpvLive): void {
  setLiveSig((all) => ({ ...all, [bridgeRowId]: fn(all[bridgeRowId] ?? empty()) }));
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);

const HEX = /^#[0-9a-f]{6}$/i;

/** Parse a `state` frame's `palettes`; null when the frame has none (an older OPVisual). Pure; exported for tests. */
export function parsePalettes(raw: unknown): OpvPalette[] | null {
  if (!Array.isArray(raw)) return null;
  const out: OpvPalette[] = [];
  for (const p of raw) {
    if (!p || typeof p !== 'object') continue;
    const o = p as Record<string, unknown>;
    const id = typeof o['id'] === 'string' ? o['id'].trim() : '';
    if (!id) continue;
    const swatch = Array.isArray(o['swatch']) ? (o['swatch'] as unknown[]).filter((c): c is string => typeof c === 'string' && HEX.test(c)) : [];
    out.push({ id, name: typeof o['name'] === 'string' && o['name'].trim() ? o['name'].trim() : id, swatch });
  }
  return out;
}

/** Parse a `state` frame's lanes. Pure; exported for tests. */
export function parseLanes(raw: unknown): OpvLaneLive[] {
  if (!Array.isArray(raw)) return [];
  const out: OpvLaneLive[] = [];
  for (const l of raw) {
    if (!l || typeof l !== 'object') continue;
    const o = l as Record<string, unknown>;
    const lane = num(o['lane']);
    if (lane === null || !Number.isInteger(lane) || lane < 1) continue;
    out.push({
      lane,
      assetId: typeof o['asset_id'] === 'string' ? o['asset_id'] : null,
      onAir: o['on_air'] === true,
      armed: o['armed'] === true,
      enabled: bool(o['enabled']),
      opacity: num(o['opacity']),
      blend: typeof o['blend'] === 'string' ? o['blend'] : null,
      speed: num(o['speed']),
    });
  }
  return out;
}

function applyState(cur: OpvLive, f: Record<string, unknown>, at: number): OpvLive {
  const look: Partial<Record<OpvLookParam, number>> = {};
  const lk = f['look'];
  if (lk && typeof lk === 'object') {
    for (const p of ['brightness', 'hue', 'sat', 'bloom'] as const) {
      const v = num((lk as Record<string, unknown>)[p]);
      if (v !== null) look[p] = v;
    }
  }
  const cs = f['cam_shake'];
  const camShake = cs && typeof cs === 'object'
    ? { on: (cs as Record<string, unknown>)['on'] === true, amount: num((cs as Record<string, unknown>)['amount']) ?? 0 }
    : null;
  return {
    ...cur,
    stateAt: at,
    palettes: parsePalettes(f['palettes']) ?? cur.palettes,
    palette: typeof f['palette'] === 'string' ? f['palette'] : 'palette' in f ? null : cur.palette,
    lanes: parseLanes(f['lanes']),
    blackout: bool(f['blackout']),
    master: num(f['master']),
    look,
    camShake,
  };
}

/**
 * One `opvisual_status` frame from a socket. `bridgeRowId` is the row whose
 * socket delivered it, which is the bridge OPVisual is attached to.
 */
export function ingestOpvStatus(bridgeRowId: string, frame: Record<string, unknown>): void {
  const kind = frame['kind'];
  const now = Date.now();
  if (kind === 'state') {
    update(bridgeRowId, (cur) => applyState(cur, frame, now));
  } else if (kind === 'heartbeat') {
    const fps = num(frame['fps']);
    const dropped = num(frame['dropped']);
    update(bridgeRowId, (cur) => {
      const hist = dropped === null ? cur.dropHistory
        : [...cur.dropHistory.filter(([t]) => now - t <= DROP_WINDOW_MS + 2000), [now, dropped] as [number, number]];
      return { ...cur, fps, heartbeatAt: now, dropHistory: hist };
    });
  } else if (kind === 'grid_fired') {
    const col = num(frame['col']);
    const row = num(frame['row']);
    if (col !== null && row !== null) update(bridgeRowId, (cur) => ({ ...cur, landedCol: { ...cur.landedCol, [row]: col } }));
  } else if (kind === 'grid_column') {
    const col = num(frame['col']);
    if (col !== null) {
      update(bridgeRowId, (cur) => {
        const landedCol: Record<number, number> = {};
        for (let r = 1; r <= 5; r++) landedCol[r] = col;
        return { ...cur, landedCol };
      });
    }
  }
}

/**
 * OPBridge's remembered snapshot from `GET /opvisual/state` (the listing
 * poll). `stamped` is `{frame, at_unix_ms}` or null. A remembered `state` is
 * applied only if it is newer than what the sockets already gave us.
 */
export function ingestOpvStatePoll(bridgeRowId: string, connected: boolean | undefined, stamped: unknown): void {
  const now = Date.now();
  update(bridgeRowId, (cur) => {
    let next: OpvLive = { ...cur, polledConnected: typeof connected === 'boolean' ? connected : null, polledAt: now };
    if (stamped && typeof stamped === 'object') {
      const s = stamped as Record<string, unknown>;
      const frame = s['frame'];
      const at = num(s['at_unix_ms']) ?? 0;
      if (frame && typeof frame === 'object' && at > cur.stateAt) next = applyState(next, frame as Record<string, unknown>, at);
    }
    return next;
  });
}

/** The live record for the machine `target` names (raw: check `opvLiveUsable`). */
export function opvLive(target: string | undefined): OpvLive | null {
  const row = target ? resolveBridge(target) : null;
  return row ? liveSig()[row.id] ?? null : null;
}

/** Whether OPVisual is believed to be there now. Pure over `now`; exported for tests. */
export function opvLiveUsable(l: OpvLive | null, now = Date.now()): boolean {
  if (!l || l.stateAt === 0) return false;
  if (now - l.heartbeatAt <= ALIVE_MS) return true;
  return l.polledConnected === true && now - l.polledAt <= POLL_TRUST_MS;
}

/** Frames dropped in the last `DROP_WINDOW_MS`, from the running totals. */
export function recentDropped(l: OpvLive, now = Date.now()): number {
  const inWin = l.dropHistory.filter(([t]) => now - t <= DROP_WINDOW_MS);
  if (inWin.length === 0) return 0;
  const before = l.dropHistory.filter(([t]) => now - t > DROP_WINDOW_MS).pop();
  const base = before ? before[1] : inWin[0]![1];
  return Math.max(0, inWin[inWin.length - 1]![1] - base);
}

/**
 * Which column is ON AIR for a layer, given OPVisual's report and the listing.
 * The lane reports the asset playing; the cell is the one in that row holding
 * that asset. If the same clip sits in several columns of the row, the column
 * a fire last LANDED in wins, else the first. Pure; exported for tests.
 */
export function onAirCol(
  live: OpvLive,
  layer: number,
  assetAt: (col: number, row: number) => string | null,
  cols: number,
): number | null {
  const lane = live.lanes.find((l) => l.lane === layer);
  if (!lane || !lane.onAir || !lane.assetId) return null;
  const hits: number[] = [];
  for (let c = 1; c <= cols; c++) if (assetAt(c, layer) === lane.assetId) hits.push(c);
  if (hits.length === 0) return null;
  const landed = live.landedCol[layer];
  if (landed !== undefined && hits.includes(landed)) return landed;
  return hits[0]!;
}

/** Whether OPVisual reports a bar-snapped fire waiting on this layer. */
export function laneArmed(live: OpvLive, layer: number): boolean {
  return live.lanes.find((l) => l.lane === layer)?.armed === true;
}

export function laneLive(live: OpvLive, layer: number): OpvLaneLive | null {
  return live.lanes.find((l) => l.lane === layer) ?? null;
}
